import React, { useState, useEffect } from 'react';
import { PreReleaseProject } from '../../services/preReleaseService';
import { 
  InterestedUser, 
  EmailTemplateData, 
  WebsiteMessageData,
  postReleaseNotificationService 
} from '../../services/postReleaseNotificationService';
import { NotificationValidation, NotificationValidationError, NotificationServiceError } from '../../utils/notificationValidation';
import '../../styles/components/ReleaseNotificationModal.css';

interface ReleaseNotificationModalProps {
  project: PreReleaseProject;
  isOpen: boolean;
  onClose: () => void;
  onEmailTemplateGenerate: (templateData: EmailTemplateData) => void;
  onWebsiteMessageSend: (messageData: WebsiteMessageData) => void;
}

interface UserStatistics {
  totalUsers: number;
  willingToPayCount: number;
  committedToBuyCount: number;
  earlyBirdCount: number;
  averageInterestLevel: number;
  averageMaxPrice: number;
}

interface ErrorDetail {
  message: string;
  code?: string;
  field?: string;
  retryAction?: () => void;
}

const ReleaseNotificationModal: React.FC<ReleaseNotificationModalProps> = ({
  project,
  isOpen,
  onClose,
  onEmailTemplateGenerate,
  onWebsiteMessageSend
}) => {
  const [interestedUsers, setInterestedUsers] = useState<InterestedUser[]>([]);
  const [userStats, setUserStats] = useState<UserStatistics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ErrorDetail | null>(null);
  const [selectedMethod, setSelectedMethod] = useState<'email' | 'website' | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [retryCount, setRetryCount] = useState(0);

  // Load interested users when modal opens
  useEffect(() => {
    if (isOpen && project.id) {
      loadInterestedUsers();
    }
  }, [isOpen, project.id]);

  const validateProject = (): boolean => {
    try {
      NotificationValidation.validateProjectForNotifications(project);
      setValidationErrors({});
      return true;
    } catch (validationError: any) {
      if (validationError instanceof NotificationValidationError) {
        setValidationErrors({
          [validationError.field || 'general']: validationError.message
        });
        setError({
          message: validationError.message,
          code: validationError.code,
          field: validationError.field
        });
      }
      return false;
    }
  };

  const loadInterestedUsers = async () => {
    setLoading(true);
    setError(null);
    setValidationErrors({});
    
    try {
      // Validate project first
      if (!validateProject()) {
        return;
      }

      const users = await postReleaseNotificationService.getInterestedUsers(project.id);
      setInterestedUsers(users);
      
      // Calculate user statistics
      const stats = calculateUserStatistics(users);
      setUserStats(stats);
      setRetryCount(0); // Reset retry count on success
    } catch (err: any) {
      console.error('Error loading interested users:', err);
      
      let errorDetail: ErrorDetail;
      
      if (err instanceof NotificationValidationError) {
        errorDetail = {
          message: err.message,
          code: err.code,
          field: err.field,
          retryAction: loadInterestedUsers
        };
        setValidationErrors({
          [err.field || 'general']: err.message
        });
      } else if (err instanceof NotificationServiceError) {
        errorDetail = {
          message: `Service error: ${err.message}`,
          code: err.code,
          retryAction: loadInterestedUsers
        };
      } else {
        errorDetail = {
          message: err.message || 'Failed to load interested users',
          retryAction: loadInterestedUsers
        };
      }
      
      setError(errorDetail);
      setRetryCount(prev => prev + 1);
    } finally {
      setLoading(false);
    }
  };

  const calculateUserStatistics = (users: InterestedUser[]): UserStatistics => {
    if (users.length === 0) {
      return {
        totalUsers: 0,
        willingToPayCount: 0,
        committedToBuyCount: 0,
        earlyBirdCount: 0,
        averageInterestLevel: 0,
        averageMaxPrice: 0
      };
    }

    const willingToPayCount = users.filter(u => u.willing_to_pay).length;
    const committedToBuyCount = users.filter(u => u.committed_to_buy).length;
    const earlyBirdCount = users.filter(u => u.early_bird_commitment).length;
    
    const totalInterestLevel = users.reduce((sum, u) => sum + u.interest_level, 0);
    const averageInterestLevel = totalInterestLevel / users.length;
    
    const usersWithMaxPrice = users.filter(u => u.max_price_willing && u.max_price_willing > 0);
    const totalMaxPrice = usersWithMaxPrice.reduce((sum, u) => sum + (u.max_price_willing || 0), 0);
    const averageMaxPrice = usersWithMaxPrice.length > 0 ? totalMaxPrice / usersWithMaxPrice.length : 0;

    return {
      totalUsers: users.length,
      willingToPayCount,
      committedToBuyCount,
      earlyBirdCount,
      averageInterestLevel,
      averageMaxPrice
    };
  };

  const handleEmailTemplateGenerate = async () => {
    if (!selectedMethod || selectedMethod !== 'email') return;
    
    setIsProcessing(true);
    setError(null);
    setValidationErrors({});

    try {
      // Validate inputs before proceeding
      if (!validateProject()) {
        return;
      }

      if (interestedUsers.length === 0) {
        throw new NotificationValidationError(
          'No interested users found. Cannot generate email template.',
          'NO_USERS_FOUND'
        );
      }

      NotificationValidation.validateInterestedUsers(interestedUsers);

      const templateData = await postReleaseNotificationService.generateEmailTemplate(project, interestedUsers);
      onEmailTemplateGenerate(templateData);
      onClose();
    } catch (err: any) {
      console.error('Error generating email template:', err);
      
      let errorDetail: ErrorDetail;
      
      if (err instanceof NotificationValidationError) {
        errorDetail = {
          message: err.message,
          code: err.code,
          field: err.field,
          retryAction: handleEmailTemplateGenerate
        };
        setValidationErrors({
          [err.field || 'general']: err.message
        });
      } else if (err instanceof NotificationServiceError) {
        errorDetail = {
          message: `Template generation failed: ${err.message}`,
          code: err.code,
          retryAction: handleEmailTemplateGenerate
        };
      } else {
        errorDetail = {
          message: err.message || 'Failed to generate email template',
          retryAction: handleEmailTemplateGenerate
        };
      }
      
      setError(errorDetail);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleWebsiteMessageSend = async () => {
    if (!selectedMethod || selectedMethod !== 'website') return;
    
    setIsProcessing(true);
    setError(null);
    setValidationErrors({});

    try {
      // Validate inputs before proceeding
      if (!validateProject()) {
        return;
      }

      if (interestedUsers.length === 0) {
        throw new NotificationValidationError(
          'No interested users found. Cannot send website messages.',
          'NO_USERS_FOUND'
        );
      }

      NotificationValidation.validateInterestedUsers(interestedUsers);

      const successCount = await postReleaseNotificationService.sendWebsiteMessages(project, interestedUsers);
      
      if (successCount === 0) {
        throw new NotificationServiceError(
          'No messages were sent successfully. Please check the user data and try again.',
          'NO_MESSAGES_SENT',
          'sendWebsiteMessages'
        );
      }

      if (successCount < interestedUsers.length) {
        console.warn(`Only ${successCount} out of ${interestedUsers.length} messages were sent successfully.`);
      }
      
      const messageData: WebsiteMessageData = {
        recipients: interestedUsers.map(u => u.user_id),
        messageText: `🎉 ${project.title} is now available! The project you showed interest in has been officially released.`,
        messageType: 'project_release',
        projectId: project.id,
        includeProjectLink: true
      };
      
      onWebsiteMessageSend(messageData);
      onClose();
    } catch (err: any) {
      console.error('Error sending website messages:', err);
      
      let errorDetail: ErrorDetail;
      
      if (err instanceof NotificationValidationError) {
        errorDetail = {
          message: err.message,
          code: err.code,
          field: err.field,
          retryAction: handleWebsiteMessageSend
        };
        setValidationErrors({
          [err.field || 'general']: err.message
        });
      } else if (err instanceof NotificationServiceError) {
        errorDetail = {
          message: `Message sending failed: ${err.message}`,
          code: err.code,
          retryAction: handleWebsiteMessageSend
        };
      } else {
        errorDetail = {
          message: err.message || 'Failed to send website messages',
          retryAction: handleWebsiteMessageSend
        };
      }
      
      setError(errorDetail);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleMethodSelect = (method: 'email' | 'website') => {
    setSelectedMethod(method);
    setError(null);
    setValidationErrors({});
    
    // Validate that we have users before allowing method selection
    try {
      if (interestedUsers.length === 0) {
        throw new NotificationValidationError(
          'No interested users available for notification',
          'NO_USERS_AVAILABLE'
        );
      }
      
      NotificationValidation.validateInterestedUsers(interestedUsers);
    } catch (validationError: any) {
      if (validationError instanceof NotificationValidationError) {
        setValidationErrors({
          [validationError.field || 'general']: validationError.message
        });
        setError({
          message: validationError.message,
          code: validationError.code,
          field: validationError.field
        });
        setSelectedMethod(null); // Clear selection if validation fails
      }
    }
  };

  const handleProceed = () => {
    if (!selectedMethod) {
      setError({
        message: 'Please select a notification method before proceeding',
        code: 'NO_METHOD_SELECTED'
      });
      return;
    }

    // Clear any previous errors
    setError(null);
    setValidationErrors({});

    if (selectedMethod === 'email') {
      handleEmailTemplateGenerate();
    } else if (selectedMethod === 'website') {
      handleWebsiteMessageSend();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="modal-title">
      <div className="modal-content release-notification-modal">
        <div className="modal-header">
          <h2 id="modal-title">Notify Interested Users</h2>
          <button 
            className="close-button" 
            onClick={onClose} 
            disabled={isProcessing}
            aria-label="Close notification modal"
          >
            ×
          </button>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="loading-state" role="status" aria-live="polite">
              <div className="spinner" aria-hidden="true"></div>
              <p>Loading interested users...</p>
            </div>
          ) : error ? (
            <div className="error-state" role="alert" aria-live="assertive">
              <div className="error-message">
                <p>{error.message}</p>
                {error.code && (
                  <p className="error-code">Error Code: {error.code}</p>
                )}
                {error.field && (
                  <p className="error-field">Field: {error.field}</p>
                )}
              </div>
              <div className="error-actions">
                <button 
                  onClick={loadInterestedUsers} 
                  className="retry-button"
                  aria-describedby={error.code ? "error-code" : undefined}
                >
                  Try Again {retryCount > 0 && `(Attempt ${retryCount + 1})`}
                </button>
                {error.retryAction && error.retryAction !== loadInterestedUsers && (
                  <button 
                    onClick={error.retryAction} 
                    className="alt-retry-button"
                    aria-label="Alternative retry action"
                  >
                    Alternative Retry
                  </button>
                )}
              </div>
              {Object.keys(validationErrors).length > 0 && (
                <div className="validation-errors" role="alert">
                  <h4>Validation Issues:</h4>
                  {Object.entries(validationErrors).map(([field, message]) => (
                    <p key={field} className="validation-error">
                      {field !== 'general' && <strong>{field}:</strong>} {message}
                    </p>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <>
              {/* User Statistics Section */}
              <div className="user-statistics" role="region" aria-labelledby="stats-heading">
                <h3 id="stats-heading">Interested User Statistics</h3>
                {userStats && userStats.totalUsers > 0 ? (
                  <div className="stats-grid" role="grid" aria-label="User interest statistics">
                    <div className="stat-item" role="gridcell">
                      <div className="stat-number" aria-label={`${userStats.totalUsers} total interested users`}>{userStats.totalUsers}</div>
                      <div className="stat-label">Total Interested Users</div>
                    </div>
                    <div className="stat-item" role="gridcell">
                      <div className="stat-number" aria-label={`${userStats.willingToPayCount} users willing to pay`}>{userStats.willingToPayCount}</div>
                      <div className="stat-label">Willing to Pay</div>
                    </div>
                    <div className="stat-item" role="gridcell">
                      <div className="stat-number" aria-label={`${userStats.committedToBuyCount} users committed to buy`}>{userStats.committedToBuyCount}</div>
                      <div className="stat-label">Committed to Buy</div>
                    </div>
                    <div className="stat-item" role="gridcell">
                      <div className="stat-number" aria-label={`${userStats.earlyBirdCount} users with early bird interest`}>{userStats.earlyBirdCount}</div>
                      <div className="stat-label">Early Bird Interest</div>
                    </div>
                    <div className="stat-item" role="gridcell">
                      <div className="stat-number" aria-label={`Average interest level ${userStats.averageInterestLevel.toFixed(1)} out of 10`}>{userStats.averageInterestLevel.toFixed(1)}/10</div>
                      <div className="stat-label">Average Interest Level</div>
                    </div>
                    {userStats.averageMaxPrice > 0 && (
                      <div className="stat-item" role="gridcell">
                        <div className="stat-number" aria-label={`Average maximum price ${userStats.averageMaxPrice.toFixed(0)} dollars`}>${userStats.averageMaxPrice.toFixed(0)}</div>
                        <div className="stat-label">Average Max Price</div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="no-users-message" role="status">
                    <p>No users have expressed interest in this project yet.</p>
                    <p>Once users show interest, you'll be able to notify them about the release.</p>
                  </div>
                )}
              </div>

              {/* Notification Method Selection */}
              {userStats && userStats.totalUsers > 0 && (
                <div className="notification-methods" role="region" aria-labelledby="methods-heading">
                  <h3 id="methods-heading">Choose Notification Method</h3>
                  <p>Select how you'd like to notify the {userStats.totalUsers} interested users:</p>
                  
                  <div className="method-options" role="radiogroup" aria-labelledby="methods-heading">
                    <div 
                      className={`method-option ${selectedMethod === 'email' ? 'selected' : ''}`}
                      onClick={() => handleMethodSelect('email')}
                      role="radio"
                      aria-checked={selectedMethod === 'email'}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleMethodSelect('email');
                        }
                      }}
                      aria-labelledby="email-method-title"
                      aria-describedby="email-method-desc"
                    >
                      <div className="method-icon" aria-hidden="true">📧</div>
                      <div className="method-details">
                        <h4 id="email-method-title">Email Template</h4>
                        <p id="email-method-desc">Generate a downloadable email template with recipient list and pre-written content. You'll send the emails from your own email account.</p>
                        <ul>
                          <li>Complete recipient list included</li>
                          <li>Professional email template</li>
                          <li>Customizable content</li>
                          <li>Send from your email account</li>
                        </ul>
                      </div>
                    </div>

                    <div 
                      className={`method-option ${selectedMethod === 'website' ? 'selected' : ''}`}
                      onClick={() => handleMethodSelect('website')}
                      role="radio"
                      aria-checked={selectedMethod === 'website'}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleMethodSelect('website');
                        }
                      }}
                      aria-labelledby="website-method-title"
                      aria-describedby="website-method-desc"
                    >
                      <div className="method-icon" aria-hidden="true">💬</div>
                      <div className="method-details">
                        <h4 id="website-method-title">Website Messages</h4>
                        <p id="website-method-desc">Send direct messages through the platform to all interested users. Messages will appear in their notification center.</p>
                        <ul>
                          <li>Instant delivery</li>
                          <li>Built-in engagement tracking</li>
                          <li>Professional messaging system</li>
                          <li>No external email required</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Modal Footer */}
        {userStats && userStats.totalUsers > 0 && (
          <div className="modal-footer">
            <button 
              className="cancel-button" 
              onClick={onClose}
              disabled={isProcessing}
              aria-label="Cancel notification process"
            >
              Cancel
            </button>
            <button 
              className="proceed-button" 
              onClick={handleProceed}
              disabled={!selectedMethod || isProcessing}
              aria-describedby={!selectedMethod ? "method-required" : undefined}
            >
              {isProcessing ? (
                <>
                  <span className="spinner-small" aria-hidden="true"></span>
                  {selectedMethod === 'email' ? 'Generating Template...' : 'Sending Messages...'}
                </>
              ) : (
                selectedMethod === 'email' ? 'Generate Email Template' : 'Send Website Messages'
              )}
            </button>
            {!selectedMethod && (
              <div id="method-required" className="sr-only">
                Please select a notification method before proceeding
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReleaseNotificationModal;